import { EventEmitter, Injectable, Output, Directive } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { Client } from "../_models/client";
import { map } from "rxjs/operators";
import { UserData } from "../_models/user.model";
import { EntityData } from "../modules/company/models/entity.data";
import { DefaultResponseData } from "@api/http";

export interface StoreClientRequestData {
  company_name: string;
  user: {
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    phone_prefix: string;
    company_position: string;
  };
}

export interface ClientAccountManagersData {
  client_id: number;
  account_manager_ids: number[];
  account_managers: UserData[];
}

const httpOptions = {
  headers: new HttpHeaders({ "Content-Type": "application/json" }),
};

@Directive()
@Injectable()
export class ClientsService {
  private clientsUrl = "entities/clients";

  @Output() statusChange: EventEmitter<any> = new EventEmitter();

  constructor(private http: HttpClient) {}

  // GET Clients
  getClients(params = {}): Observable<any> {
    return this.http.get<any>(environment.gateway_endpoint + this.clientsUrl, {
      params: params,
    });
  }

  // GET Clients without pagination
  getAllClients(params = {}): Observable<any> {
    return this.http.get<any>(
      environment.gateway_endpoint + "entities/clients-without-pagination"
    );
  }

  // GET Client
  getClient(id): Observable<any> {
    return this.http.get<any>(
      environment.gateway_endpoint + this.clientsUrl + "/" + id
    );
  }

  getClientAccountManagers(
    id: number
  ): Observable<DefaultResponseData<ClientAccountManagersData>> {
    return this.http.get<DefaultResponseData<ClientAccountManagersData>>(
      `${environment.gateway_endpoint}${this.clientsUrl}/${id}/account-managers`
    );
  }

  updateClientAccountManagers(
    id: number,
    accountManagerIds: number[]
  ): Observable<DefaultResponseData<ClientAccountManagersData>> {
    return this.http.put<DefaultResponseData<ClientAccountManagersData>>(
      `${environment.gateway_endpoint}${this.clientsUrl}/${id}/account-managers`,
      { account_manager_ids: accountManagerIds },
      httpOptions
    );
  }

  // GET Client history
  getEntityHistory(id: number, params = {}): Observable<any> {
    return this.http.get<any>(
      environment.gateway_endpoint + "history/client/" + id,
      { params: params }
    );
  }

  getStatusTransitions(): Observable<any> {
    return this.http.get(
      environment.gateway_endpoint + "entities/client/status-transitions"
    );
  }

  // POST Create Client step 1
  storeClient(client: StoreClientRequestData): Observable<Client> {
    return this.http
      .post<DefaultResponseData<Client>>(
        environment.gateway_endpoint + this.clientsUrl,
        client,
        httpOptions
      )
      .pipe(
        map((model) => {
          return model.result;
        })
      );
  }

  // POST Create Client step 2
  createClientStep2(client: Client): Observable<any> {
    return this.http
      .post<any>(
        environment.gateway_endpoint + this.clientsUrl + "/" + client.id,
        client,
        httpOptions
      )
      .pipe(
        map((model) => {
          return model;
        })
      );
  }

  // PUT Edit Account info
  updateAccountDetails(client): Observable<any> {
    return this.http
      .put<any>(
        environment.gateway_endpoint +
          "entities/clients/user/" +
          client.entity_id,
        client,
        httpOptions
      )
      .pipe(
        map((model) => {
          return model;
        })
      );
  }

  // PUT Edit Company info
  updateCompanyDetails(client: EntityData): Observable<any> {
    return this.http
      .put<any>(
        `${environment.gateway_endpoint}${this.clientsUrl}/${client.id}`,
        client,
        httpOptions
      )
      .pipe(
        map((model) => {
          return model;
        })
      );
  }

  // POST Create User for client
  createUser(id, user: UserData): Observable<any> {
    return this.http
      .post<any>(
        environment.gateway_endpoint + this.clientsUrl + `/${id}/addUser`,
        user,
        httpOptions
      )
      .pipe(
        map((model) => {
          return model;
        })
      );
  }

  // Change client status
  updateStatusBulk(status_id, client_ids, reason = null): Observable<any> {
    const body = { status_id: status_id, entity_ids: client_ids };
    if (reason) {
      body["reason"] = reason;
    }
    const response = this.http.patch<any>(
      environment.gateway_endpoint + "entities/clients",
      body,
      httpOptions
    );

    this.statusChange.emit();
    return response;
  }

  // Log in as specified client
  switchToAccount(account_info) {
    return this.http
      .post<any>(
        environment.gateway_endpoint + "switch-to-client",
        account_info,
        httpOptions
      )
      .pipe(
        map((data) => {
          if (data && data.result.token) {
            // serialize and encode session data to use on portal
            const marketplaceUrlPathArray = environment.marketplace.split("/");
            const protocol = marketplaceUrlPathArray[0];
            const host = marketplaceUrlPathArray[2];
            const goToMarketplaceUrl = protocol + "//" + host;
            const response = btoa(JSON.stringify(data.result));
            window.open(
              goToMarketplaceUrl + "/sso/?token=" + response,
              "_blank"
            );
            // window.location.href =
            //   goToMarketplaceUrl + '/sso/?token=' + response;
          }
        })
      );
  }

  /**
   * Get number of clients pending approval
   */
  getPendingCount(): Observable<any> {
    return this.http.get(
      environment.gateway_endpoint + this.clientsUrl + "/pending/count",
      { params: { "low-priority": "low-priority" } }
    );
  }
}
